/*
 * Description:
 * ---------------------
 * Sequence-model (CNN) shadow-prediction monitor — TRIAL FEATURE.
 *
 * Polls GET /seqforecast every 5s and shows, per WAN, the CNN's live
 * predicted degradation probability alongside the baseline OLS heuristic's
 * own verdict at the same instant, so an operator can see at a glance
 * whether the two agree. SHADOW MODE ONLY: nothing here reflects or
 * influences actual routing decisions — see cloud-anchor/pkg/seqforecast
 * and cloud-anchor/pkg/forecast for the promotion-gate policy this respects.
 *
 * ! file SeqForecastPanel.js
 * ---------------
 * \author: Tolga O. Atalay
 * \Affiliation: A2 Labs LLC / A12XT Inc.
 * \date: 2026
 */

import React, { useEffect, useState } from "react";
import { fetchWithTimeout } from "../utils/fetchWithTimeout";
import { WanLabel } from "./WanNames";

const HISTORY_CAP = 120; // ~10 min at 5s polling cadence

const riskColor = (p) => {
  if (p == null) return "#94a3b8";
  if (p < 0.2) return "#16a34a";
  if (p < 0.4) return "#65a30d";
  if (p < 0.6) return "#ca8a04";
  if (p < 0.8) return "#ea580c";
  return "#dc2626";
};

const riskBg = (p) => {
  if (p == null) return "#f1f5f9";
  if (p < 0.2) return "rgba(22,163,74,0.10)";
  if (p < 0.4) return "rgba(101,163,13,0.10)";
  if (p < 0.6) return "rgba(202,138,4,0.10)";
  if (p < 0.8) return "rgba(234,88,12,0.10)";
  return "rgba(220,38,38,0.10)";
};

const trendColor = (trend) => {
  if (trend === "degrading") return "#dc2626";
  if (trend === "improving") return "#16a34a";
  return "#64748b";
};

function MiniSpark({ points, color, height = 32, width = 160 }) {
  const valid = (points || []).filter((p) => p.value != null && isFinite(p.value));
  if (valid.length < 2) {
    return (
      <div style={{
        height, width, display: "flex", alignItems: "center",
        justifyContent: "center", color: "#cbd5e1", fontSize: "0.7rem", fontStyle: "italic",
      }}>
        building trend…
      </div>
    );
  }
  const xs = valid.map((p) => p.ts);
  const xMin = Math.min(...xs);
  const xMax = Math.max(...xs);
  const yMin = 0;
  const yMax = 1; // probability space
  const sx = (x) => 2 + ((x - xMin) / Math.max(1, xMax - xMin)) * (width - 4);
  const sy = (y) => height - 2 - ((y - yMin) / (yMax - yMin)) * (height - 4);
  const d = valid
    .map((p, i) => `${i === 0 ? "M" : "L"}${sx(p.ts).toFixed(1)} ${sy(p.value).toFixed(1)}`)
    .join(" ");
  const fill = `${d} L${sx(xMax).toFixed(1)} ${(height - 2).toFixed(1)} L${sx(xMin).toFixed(1)} ${(height - 2).toFixed(1)} Z`;
  const last = valid[valid.length - 1].value;

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <svg width={width} height={height} style={{ display: "block", flexShrink: 0 }}>
        <path d={fill} fill={color} fillOpacity={0.10} stroke="none" />
        <path d={d} fill="none" stroke={color} strokeWidth="1.6" strokeLinejoin="round" strokeLinecap="round" />
      </svg>
      <span style={{ fontSize: "0.78rem", fontWeight: 700, color, whiteSpace: "nowrap" }}>
        {(last * 100).toFixed(1)}%
      </span>
    </div>
  );
}

function WANRow({ wan, historyByWan }) {
  const history = historyByWan[wan.wan] || [];
  const agree = wan.heuristic_pred_degrade === (wan.seq_prob >= 0.5);

  // A row too narrow for one line wraps, the verdict to the right.
  return (
    <div style={{
      display: "flex", flexWrap: "wrap", alignItems: "center", gap: 16, padding: "14px 18px",
      border: "1px solid #e2e8f0", borderRadius: 10, background: "#fff", marginBottom: 10,
    }}>
      <div style={{ minWidth: 90 }}>
        <div style={{ fontWeight: 800, fontSize: "0.95rem", color: "#1e293b" }}><WanLabel iface={wan.wan} /></div>
        <div style={{ fontSize: "0.68rem", color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.06em" }}>
          interface
        </div>
      </div>

      <div style={{
        padding: "6px 12px", borderRadius: 8,
        background: riskBg(wan.seq_prob), color: riskColor(wan.seq_prob),
        fontWeight: 800, fontSize: "0.9rem", minWidth: 92, textAlign: "center",
      }}>
        {(wan.seq_prob * 100).toFixed(1)}% risk
      </div>

      <MiniSpark
        points={history.map((h) => ({ ts: h.ts, value: h.seqProb }))}
        color={riskColor(wan.seq_prob)}
      />

      <div style={{ flex: 1 }} />

      <div style={{ textAlign: "right" }}>
        <div style={{ fontSize: "0.72rem", color: "#94a3b8" }}>heuristic (OLS)</div>
        <div style={{ fontWeight: 700, color: trendColor(wan.heuristic_trend) }}>
          {wan.heuristic_predicted_quality?.toFixed?.(0) ?? "—"} · {wan.heuristic_trend || "unknown"}
        </div>
      </div>

      <div style={{
        marginLeft: "auto", padding: "4px 10px", borderRadius: 6, fontSize: "0.7rem", fontWeight: 700,
        background: agree ? "rgba(22,163,74,0.10)" : "rgba(202,138,4,0.10)",
        color: agree ? "#16a34a" : "#a16207", minWidth: 76, textAlign: "center",
      }}>
        {agree ? "agree" : "differ"}
      </div>
    </div>
  );
}

export default function SeqForecastPanel({ nodeApiUrl, selectedNode }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [historyByWan, setHistoryByWan] = useState({});

  useEffect(() => {
    if (!selectedNode) { setData(null); setLoading(false); setHistoryByWan({}); return; }

    const controller = new AbortController();
    setHistoryByWan({});
    setLoading(true);

    const doFetch = async () => {
      const url = nodeApiUrl("/seqforecast");
      if (!url) return;
      try {
        const res = await fetchWithTimeout(url, { signal: controller.signal });
        if (!res.ok) throw new Error(`${res.status}`);
        const body = await res.json();
        if (controller.signal.aborted) return;
        setData(body);
        setError("");

        const now = Date.now();
        const readings = Array.isArray(body?.readings) ? body.readings : [];
        setHistoryByWan((prev) => {
          const next = { ...prev };
          const present = new Set();
          for (const w of readings) {
            if (!w?.wan) continue;
            present.add(w.wan);
            const arr = next[w.wan] ? next[w.wan].slice() : [];
            arr.push({ ts: now, seqProb: w.seq_prob });
            if (arr.length > HISTORY_CAP) arr.splice(0, arr.length - HISTORY_CAP);
            next[w.wan] = arr;
          }
          for (const k of Object.keys(next)) {
            if (!present.has(k)) delete next[k];
          }
          return next;
        });
      } catch (err) {
        if (err.name === "AbortError") return;
        console.error("[SeqForecastPanel] fetch error:", err);
        if (!controller.signal.aborted) setError(err.message || "Failed to load seqforecast data");
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    };

    doFetch();
    const timer = setInterval(doFetch, 5000);
    return () => { controller.abort(); clearInterval(timer); };
  }, [selectedNode, nodeApiUrl]);

  if (loading && !data) {
    return <div style={{ padding: 24, color: "#64748b" }}>Loading sequence-model predictor…</div>;
  }
  if (error) {
    return <div style={{ padding: 24, color: "#dc2626" }}>Error: {error}</div>;
  }
  if (!data?.enabled) {
    return (
      <div style={{
        padding: 24, borderRadius: 10, background: "#f8fafc",
        border: "1px dashed #cbd5e1", color: "#64748b", maxWidth: 640,
      }}>
        <div style={{ fontWeight: 800, color: "#1e293b", marginBottom: 6 }}>
          Sequence-model predictor not active on this node
        </div>
        <div style={{ fontSize: "0.85rem" }}>
          This is an opt-in trial feature. Set <code>NABX_SEQFORECAST_MODEL</code> to an
          exported weights JSON and <code>NABX_SEQFORECASTSET_ENABLED=true</code> in the
          edge's environment, then restart <code>nabx-edge</code>.
        </div>
      </div>
    );
  }

  const readings = Array.isArray(data.readings) ? data.readings : [];

  return (
    <div style={{ maxWidth: 900 }}>
      <div style={{
        display: "flex", alignItems: "center", gap: 10, marginBottom: 16,
        padding: "10px 14px", borderRadius: 8, background: "rgba(202,138,4,0.08)",
        border: "1px solid rgba(202,138,4,0.25)", color: "#a16207", fontSize: "0.82rem", fontWeight: 600,
      }}>
        ⚠ TRIAL / SHADOW MODE — this predictor is read-only and does not influence
        routing. It is compared here against the baseline heuristic for evaluation only.
      </div>

      <div style={{ display: "flex", gap: 24, marginBottom: 18, flexWrap: "wrap" }}>
        <div>
          <div style={{ fontSize: "0.7rem", color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.06em" }}>
            Model
          </div>
          <div style={{ fontWeight: 800, color: "#1e293b" }}>{data.model_arch}</div>
        </div>
        <div>
          <div style={{ fontSize: "0.7rem", color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.06em" }}>
            Window
          </div>
          <div style={{ fontWeight: 800, color: "#1e293b" }}>{data.window_len} samples</div>
        </div>
        <div>
          <div style={{ fontSize: "0.7rem", color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.06em" }}>
            Held-out test ROC-AUC
          </div>
          <div style={{ fontWeight: 800, color: "#1e293b" }}>
            {data.held_out_auc != null ? data.held_out_auc.toFixed(3) : "—"}
          </div>
        </div>
      </div>

      {readings.length === 0 ? (
        <div style={{ color: "#94a3b8", fontStyle: "italic" }}>
          No WAN feature windows available yet — waiting for enough health history to build a window.
        </div>
      ) : (
        readings.map((w) => <WANRow key={w.wan} wan={w} historyByWan={historyByWan} />)
      )}
    </div>
  );
}
